import zoomSdk from "@zoom/appssdk";
import type { AppState, AssignmentPlan, BreakoutStage, HoogahAuth, MatchedBy, OperatorRole } from "./types.js";
import {
  AuthExpiredError,
  completeAssignmentRound,
  completeLiteRound,
  createRoundPairs,
  debugLog,
  devAuth,
  exchangeContextToken,
  fetchAssignmentPlan,
  fetchEventContext,
  fetchLiteAssignmentPlan,
  fetchLiteContext,
  fetchLiteMeetingIdentities,
  fetchMeetingIdentities,
  saveLiteMeetingIdentity,
  saveMeetingIdentity,
  saveZoomNameAlias,
  syncParticipants,
  updateAssignmentRun,
  updateLiteAssignmentRun,
  ApiError,
  BASE_URL,
} from "./api.js";
import type { AssignmentRunUpdate, RoomAssignmentResult } from "./api.js";
import {
  cleanupTargetUuids,
  occupantsByRoom,
  resolveResumePoint,
  stageMovedMessage,
  stageOutOfViewMessage,
} from "./breakout-resume.js";
import type { LiveBreakoutRoomOccupant, LiveBreakoutRooms } from "./breakout-resume.js";
import {
  renderAssignmentPreview,
  hostOnlyStepGuidance,
  renderAssignmentActions,
  bindBreakoutButtons,
  bindRoomAccordion,
  bindAbsentToggle,
  bindPlacementControls,
  type MatchingContext,
} from "./views/assignment-preview.js";
import { escapeHtml } from "./security/escape-html.js";
import { runAuthorizeFlow } from "./security/authorize-flow.js";
import { authErrorMessage } from "./security/auth-error-message.js";
import { serializeError } from "./serialize-error.js";
import { truncateName } from "./truncate.js";
import { isNamePrefixMatch, isNameSubset, normalizeName } from "./matching/name-normalize.js";
import { buildLiveIndex } from "./matching/live-index.js";
import { absentPlanParticipants, buildLiveUnplacedRows, unplacedPlanOptions } from "./matching/reverse-match.js";
import { breakoutErrorMessage, HOST_ONLY_STEP_MESSAGE, isAlreadyDoneCode, zoomErrorCode } from "./breakout-errors.js";
import { buildBreakoutConfig } from "./breakout-config.js";
import type { RoomParticipant, LiveParticipantOption } from "./types.js";

const state: AppState = {
  auth: null,
  eventId: null,
  eventContext: null,
  syncResult: null,
  plan: null,
  selectedRoundId: null,
  breakoutStage: "idle",
  isLite: false,
  liteContext: null,
  zoomMeetingId: "",
  meetingUuid: "",
  unmatchedAssignments: [],
  selfRole: null,
};

let emailCapabilityGranted = false;
let userContextGranted = false;
let breakoutRoomByName = new Map<string, string>();
let breakoutLiveById = new Map<string, { uuid: string; screenName: string }>();
let breakoutLiveByName = new Map<string, { uuid: string; screenName: string }>();
let breakoutLiveByEmail = new Map<string, { uuid: string; screenName: string }>();
let breakoutLiveByNormalizedName = new Map<string, { uuid: string; screenName: string }>();
let breakoutLiveEntriesByUuid = new Map<string, { uuid: string; screenName: string }>();
let liveParticipantsRaw: LiveParticipantOption[] = [];
// Hosts and co-hosts, by roster role. Excluded from the partial-name tier only
// — stronger tiers can still place an organiser who is genuinely in the plan.
let operatorLiveUuids = new Set<string>();

/**
 * Who is running the meeting, for diagnostics. Replaces the operator's own role
 * in logs: getUserContext is refused for this app (80004/app_not_support), and
 * the roster cannot say which entry is "me" — only who holds which role.
 */
function operatorNames(): string[] {
  return liveParticipantsRaw.filter((l) => operatorLiveUuids.has(l.uuid)).map((l) => l.screenName);
}
// Room name -> who Zoom says is inside it right now. Empty for a co-host, whose
// client Zoom refuses this detail to — see occupantsByRoom(). A room missing
// from this map is one this client cannot see into, not an empty one.
let liveRoomOccupants = new Map<string, LiveBreakoutRoomOccupant[]>();

let breakoutRoomAssignments: RoomAssignmentResult[] = [];
let breakoutAssignedCount = 0;
let breakoutUnassignedCount = 0;
let breakoutErrors: string[] = [];

// Live participants already used by a placement this round — prevents two
// different plan participants ever claiming the same human (VE-015 AC-4).
let claimedLiveUuids = new Set<string>();

// Organiser-confirmed identity, keyed by attendeeKey(). Deliberately NOT reset
// by resetBreakoutState() — a manual decision holds for the rest of the
// meeting (VE-015 AC-8) and survives a reassignment (VE-015 AC-9/AC-10).
//
// Lite mirrors this to the server (loadMeetingIdentities/rememberPlacement), so
// it is shared by everyone driving the meeting rather than trapped in the tab
// that made the decision. Held in memory as well because every match runs
// through it — this map is the read path, the server is the durable copy.
const manualPlacements = new Map<string, LiveParticipantOption>();

// uuid -> lowercased email, filled in as Zoom delivers getMeetingParticipantsEmail
// consent results. NOT reset by resetBreakoutState() or per-round rebuilds, so
// round 2+ has emails available instantly instead of waiting on the event again.
const knownEmailsByUuid = new Map<string, string>();

// True once getMeetingParticipantsEmail() has been called this meeting session.
// Zoom re-prompts every participant on every call — it does not remember a
// prior grant — so calling it again each round means asking everyone to
// re-consent on every single round. Requesting once and relying on
// knownEmailsByUuid (above) for later rounds avoids that repeat prompt.
let emailConsentRequested = false;

// Set when a manual place/reassign action fails, shown as a banner until the
// next successful render.
let manualActionError: string | null = null;

// Set when deleteRoundRooms() could not clear the meeting. Deliberately NOT
// reset by resetBreakoutState(): the round it belongs to is already being torn
// down when the warning is raised, and it has to outlive that teardown to reach
// the round selector the operator lands on next.
let roundCleanupWarning: string | null = null;

function resetBreakoutState(): void {
  state.breakoutStage = "idle";
  breakoutRoomByName = new Map();
  liveRoomOccupants = new Map();
  breakoutLiveById = new Map();
  breakoutLiveByName = new Map();
  breakoutLiveByEmail = new Map();
  breakoutLiveByNormalizedName = new Map();
  breakoutLiveEntriesByUuid = new Map();
  liveParticipantsRaw = [];
  breakoutRoomAssignments = [];
  breakoutAssignedCount = 0;
  breakoutUnassignedCount = 0;
  breakoutErrors = [];
  claimedLiveUuids = new Set();
  manualActionError = null;
  state.unmatchedAssignments = [];
}

/** Stable identity for a plan participant, used to key manual overrides across rounds. */
function attendeeKeyOf(p: RoomParticipant): string {
  if (p.user_id) return `uid:${p.user_id}`;
  if (p.pairing_id) return `pid:${p.pairing_id}`;
  if (p.email) return `email:${p.email.trim().toLowerCase()}`;
  return `name:${normalizeName(p.display_name)}`;
}

interface LiveMatch {
  entry: LiveParticipantOption;
  matchedBy: MatchedBy;
}

/** Looks up a plan participant among live meeting participants — a known Zoom session or
 *  email trusted outright (VE-015 AC-1/AC-3), exact then normalized name as fallback
 *  (VE-015 AC-2, VE-016), then any of their saved past aliases (VE-017 AC-2/AC-3 — checked
 *  after normalized name but ahead of the partial tiers, since a saved alias is an
 *  organiser-confirmed exact match and should outrank a guess, but must never outrank a
 *  verified email), then partial name as a last resort. Callers check whether the returned
 *  candidate is already claimed (VE-015 AC-4); the partial tiers are the one exception,
 *  since they need the unclaimed set to tell an unambiguous match from a guess. */
function findRawLiveCandidate(p: RoomParticipant): LiveMatch | null {
  const planZoomId = p.zoom_participant_id ?? "";
  if (planZoomId) {
    const byId = breakoutLiveById.get(planZoomId);
    if (byId) return { entry: byId, matchedBy: "zoom_session" };
  }

  const planEmail = (p.email ?? "").trim().toLowerCase();
  if (planEmail) {
    const byEmail = breakoutLiveByEmail.get(planEmail);
    if (byEmail) return { entry: byEmail, matchedBy: "email" };
  }

  const planNameKey = (p.display_name ?? "").trim().toLowerCase();
  if (planNameKey) {
    const byName = breakoutLiveByName.get(planNameKey);
    if (byName) return { entry: byName, matchedBy: "name" };
  }

  const planNormalized = normalizeName(p.display_name);
  if (planNormalized) {
    const byNormalized = breakoutLiveByNormalizedName.get(planNormalized);
    if (byNormalized) return { entry: byNormalized, matchedBy: "normalized_name" };
  }

  for (const known of p.known_names ?? []) {
    const knownKey = known.trim().toLowerCase();
    const byKnownExact = knownKey && breakoutLiveByName.get(knownKey);
    if (byKnownExact) return { entry: byKnownExact, matchedBy: "alias" };
    const knownNormalized = normalizeName(known);
    const byKnownNormalized = knownNormalized && breakoutLiveByNormalizedName.get(knownNormalized);
    if (byKnownNormalized) return { entry: byKnownNormalized, matchedBy: "alias" };
  }

  // Weakest tier, and the only one that inspects claims: a plan name that is a
  // subset of a live name ("Alex" vs "Alex Rivera"), which the sheet produces
  // whenever an organiser types first names only. Ambiguity is resolved by
  // refusing to guess — two candidates named "Alex" leave the row unmatched for
  // the organiser to place by hand (VE-015 AC-7).
  // Attendees are preferred over hosts and co-hosts: an organiser's display name
  // collides often enough to block real matches (an organiser called "Durga VVN"
  // made the plan's "Durga" ambiguous against the actual participant "Naga
  // Durga"). Preferred, not excluded — an organiser who is genuinely in the plan
  // still has to place, and a host named "Sahil Renapurkar" against the plan's
  // "Sahil" has no stronger tier that can reach them. Roster role comes free with
  // getMeetingParticipants — getUserContext is not granted to this app.
  const partialPool = liveParticipantsRaw.filter(
    (live) => !claimedLiveUuids.has(live.uuid) && isNameSubset(p.display_name, live.screenName),
  );
  const attendeeCandidates = partialPool.filter((live) => !operatorLiveUuids.has(live.uuid));
  const partialCandidates = attendeeCandidates.length > 0 ? attendeeCandidates : partialPool;
  if (partialCandidates.length === 1) {
    return { entry: partialCandidates[0], matchedBy: "partial_name" };
  }

  // Last tier: a Zoom name that compresses the plan name ("Khushi" vs
  // "khushiy"). Same ambiguity rule as above — more than one candidate is handed
  // back to the host/co-host to place by hand rather than guessed at.
  const compactPool = liveParticipantsRaw.filter(
    (live) => !claimedLiveUuids.has(live.uuid) && isNamePrefixMatch(p.display_name, live.screenName),
  );
  const compactAttendees = compactPool.filter((live) => !operatorLiveUuids.has(live.uuid));
  const compactCandidates = compactAttendees.length > 0 ? compactAttendees : compactPool;
  if (compactCandidates.length === 1) {
    return { entry: compactCandidates[0], matchedBy: "partial_name" };
  }

  return null;
}

/** Zoom delivers getMeetingParticipantsEmail() results asynchronously, one participant at
 *  a time, via this event — never inline on the getMeetingParticipants() response. Feeds
 *  both the per-round lookup and the cross-round knownEmailsByUuid store. */
function handleParticipantEmailEvent(event: {
  participantUUID: string;
  participantEmail?: string;
  errorMessage?: string;
}): void {
  // Never log the address itself — debug lines are POSTed to the backend and
  // rendered on screen. Presence and Zoom's own reason are enough to tell a
  // withheld consent from a guest from an event that never arrived at all.
  const uuidPrefix = (event.participantUUID ?? "").slice(0, 8);
  if (!event.participantEmail) {
    // Zoom only returns verified addresses from signed-in users; guests and
    // unverified accounts arrive here with errorMessage set instead.
    void debugLog("email.event.withheld", {
      uuid: uuidPrefix,
      reason: event.errorMessage ?? "",
    });
    return;
  }
  const email = event.participantEmail.trim().toLowerCase();
  knownEmailsByUuid.set(event.participantUUID, email);
  const entry = breakoutLiveEntriesByUuid.get(event.participantUUID);
  if (entry) breakoutLiveByEmail.set(email, entry);
  void debugLog("email.event.received", {
    uuid: uuidPrefix,
    // False means the address landed before the roster was indexed; it still
    // reaches the next round through knownEmailsByUuid.
    indexedNow: Boolean(entry),
    known: knownEmailsByUuid.size,
  });
}

/** Resolves who a plan participant actually is in the live meeting: a remembered organiser
 *  decision wins outright (VE-015 AC-8), otherwise the automatic cascade — but never a live
 *  person already claimed by someone else this round. */
function resolveLiveEntry(p: RoomParticipant): LiveMatch | null {
  const key = attendeeKeyOf(p);
  const remembered = rememberedLiveEntry(key);
  if (remembered && !claimedLiveUuids.has(remembered.uuid)) {
    return { entry: remembered, matchedBy: "manual" };
  }
  const raw = findRawLiveCandidate(p);
  if (raw && !claimedLiveUuids.has(raw.entry.uuid)) return raw;
  return null;
}

/**
 * The live identity behind a remembered decision, or null when nobody in the
 * meeting answers to it any more.
 *
 * The stored Zoom participant UUID dies the moment that person rejoins — Zoom
 * mints them a fresh one — and a decision now outlives the tab that made it, so
 * a stale UUID is ordinary rather than rare. Handing one to Zoom fails the
 * assign and drops the attendee into the unmatched list, when plain name
 * matching would have found them sitting right there. The screen name the
 * organiser confirmed is stored beside the UUID for exactly this: re-resolve
 * through it, and only give up if that finds nobody either.
 */
function rememberedLiveEntry(key: string): LiveParticipantOption | null {
  const remembered = manualPlacements.get(key);
  if (!remembered) return null;
  if (breakoutLiveById.has(remembered.uuid)) return remembered;
  return (
    breakoutLiveByName.get(remembered.screenName.trim().toLowerCase()) ??
    breakoutLiveByNormalizedName.get(normalizeName(remembered.screenName)) ??
    null
  );
}

/**
 * Everyone already sitting in a breakout room.
 *
 * Two sources on purpose: claimedLiveUuids is this session's live record, and
 * the plan's zoom_participant_id is the server-persisted one that survives the
 * resetBreakoutState() a Lite Refresh runs (loadLiteAssignmentPlan) — without
 * it, a mid-round Refresh would forget every placement (VE-023 AC-7).
 */
function placedLiveUuids(): Set<string> {
  const placed = new Set(claimedLiveUuids);
  for (const room of state.plan?.rooms ?? []) {
    for (const p of room.participants) {
      if (p.zoom_participant_id) placed.add(p.zoom_participant_id);
    }
  }
  return placed;
}

function unclaimedLiveOptions(placed: Set<string>): LiveParticipantOption[] {
  // Host/co-host are never attendees to place into a room, so they never
  // belong in this list either — same operatorLiveUuids exclusion the
  // auto-match cascade already uses (main.ts:184, 196).
  return liveParticipantsRaw.filter((l) => !placed.has(l.uuid) && !operatorLiveUuids.has(l.uuid));
}

/**
 * Live UUIDs of attendees ParticipantSyncService tied to a Hoogah user for
 * this event (VE-026 AC-1/AC-2) — full events only. Correlated through Zoom's
 * numeric participantId, the field both breakoutLiveById and
 * MatchedParticipant.zoom_participant_id are keyed by; a matched entry with
 * no live counterpart (already left, or synced before this roster read) is
 * silently dropped rather than guessed at.
 */
function registeredLiveUuids(): Set<string> {
  const ids = new Set<string>();
  for (const m of state.syncResult?.matched ?? []) {
    const entry = breakoutLiveById.get(m.zoom_participant_id);
    if (entry) ids.add(entry.uuid);
  }
  return ids;
}

function buildMatchingContext(): MatchingContext {
  const placed = placedLiveUuids();
  const plan = state.plan;
  // Full events restrict the reverse-match pool to registered attendees
  // (AC-1/AC-7); Lite keeps matching everyone, as VE-023 already does.
  const registered = state.isLite ? null : registeredLiveUuids();
  const reverseLive = registered ? liveParticipantsRaw.filter((l) => registered.has(l.uuid)) : liveParticipantsRaw;
  // One input object, two derived lists — the absentee list must see exactly the
  // same roster and placements as the suggestions, or the two can contradict.
  const reverseInput = plan && {
    plan,
    live: reverseLive,
    operatorUuids: operatorLiveUuids,
    placedUuids: placed,
    emailsByUuid: knownEmailsByUuid,
    manualPlacements,
    attendeeKeyOf,
  };

  return {
    unclaimedLive: unclaimedLiveOptions(placed),
    currentLiveEntry: (p) => {
      const uuid = p.zoom_participant_id;
      if (!uuid) return null;
      return { uuid, screenName: breakoutLiveById.get(uuid)?.screenName ?? p.display_name };
    },
    attendeeKey: attendeeKeyOf,
    // VE-023 (Lite) + VE-026 (full events) — was Lite-only.
    liveUnplaced: reverseInput ? buildLiveUnplacedRows(reverseInput) : [],
    unplacedPlanOptions: plan ? unplacedPlanOptions(plan, attendeeKeyOf) : [],
    absentPlanParticipants: reverseInput ? absentPlanParticipants(reverseInput) : [],
    // VE-026, full events only: present, live, but not tied to any Hoogah
    // registration for this event at all — informational, never placeable.
    unregisteredLive: registered
      ? liveParticipantsRaw.filter(
          (l) => !registered.has(l.uuid) && !placed.has(l.uuid) && !operatorLiveUuids.has(l.uuid),
        )
      : [],
    occupancyOf: (roomName) => liveRoomOccupants.get(roomName)?.length ?? null,
  };
}

function upsertRoomAssignment(
  participant: RoomParticipant,
  roomName: string,
  liveUuid: string,
  status: "assigned" | "failed" | "skipped",
  matchedBy: MatchedBy | "" = "",
): void {
  const existing = breakoutRoomAssignments.find(
    (a) =>
      (participant.user_id && a.user_id === participant.user_id) ||
      (participant.pairing_id && a.pairing_id === participant.pairing_id) ||
      (participant.email && a.email === participant.email),
  );
  if (existing) {
    existing.zoom_room_name = roomName;
    existing.zoom_participant_id = liveUuid;
    existing.assignment_status = status;
    existing.error_message = undefined;
    existing.matched_by = matchedBy;
  } else {
    breakoutRoomAssignments.push({
      user_id: participant.user_id,
      pairing_id: participant.pairing_id,
      email: participant.email,
      zoom_room_name: roomName,
      zoom_participant_id: liveUuid,
      assignment_status: status,
      matched_by: matchedBy,
    });
  }
}

function mount(html: string): void {
  const app = document.getElementById("app");
  if (app) app.innerHTML = html;
}

/**
 * Always offers a way out. Without a control here a failed step left a bare
 * alert on screen with no button, no home link and no way back — the app was
 * dead until the operator closed and reopened it, mid-meeting.
 */
function renderError(message: string): void {
  const canRecover = Boolean(state.auth);
  mount(`
    <div class="header">
      <h1>Hoogah</h1>
    </div>
    <div class="alert alert-error">${escapeHtml(message)}</div>
    ${canRecover ? `<button class="btn btn-primary" id="error-recover-btn">Back to round</button>` : ""}`);

  document.getElementById("error-recover-btn")?.addEventListener("click", () => {
    if (state.isLite) {
      void resyncLiteRound();
    } else {
      void refreshEventContextAndRender();
    }
  });
}

function renderLoading(message: string): void {
  mount(`
    <div class="loading">
      <div class="spinner"></div>
      <div>${escapeHtml(message)}</div>
    </div>`);
}

/**
 * Callback passed to every authenticated API call.
 * When a silent token refresh succeeds, this updates `state.auth.access`
 * so subsequent calls in the same session use the refreshed token.
 */
function onTokenRefreshed(
  newAccess: string,
  newAccessExpiresAt: string,
  newRefresh: string,
  newRefreshExpiresAt: string,
): void {
  if (state.auth) {
    state.auth = {
      ...state.auth,
      access: newAccess,
      accessExpiresAt: newAccessExpiresAt,
      refresh: newRefresh,
      refreshExpiresAt: newRefreshExpiresAt,
    };
  }
}

function requireAuth(): HoogahAuth {
  if (!state.auth) throw new Error("Not authenticated");
  return state.auth;
}

/** The subset of Round/LiteRound the round-selector rail needs — both carry it. */
interface RailRound {
  id: number;
  name: string;
  order: number;
  completed_at: string | null;
}

/**
 * Round-selector head shared by the full-event and Lite home screens: a filled
 * position tile, the round name, and one progress segment per round.
 *
 * Rendered flat on purpose — see the .round-rail note in index.html. Both
 * screens previously wrapped the bare name in a card, which read as a disabled
 * input and threw away the plan sequence the context already carries.
 */
function roundRailHtml(rounds: RailRound[], activeId: number, label: string): string {
  const ordered = [...rounds].sort((a, b) => a.order - b.order);
  const zeroBased = ordered.findIndex((r) => r.id === activeId);
  // A round that isn't in its own list shouldn't render "0 of n" — fall back to
  // first position rather than showing a nonsense index.
  const position = zeroBased >= 0 ? zeroBased + 1 : 1;
  const active = ordered[zeroBased] ?? ordered[0];
  const segments = ordered
    .map((r) => {
      const cls = r.id === activeId ? " now" : r.completed_at ? " done" : "";
      return `<span class="seg${cls}"></span>`;
    })
    .join("");
  // Capped text for display, full name on hover — CSS then ellipsizes further
  // if the panel is narrower than even the capped string.
  const fullName = active?.name ?? "";
  const shownName = escapeHtml(truncateName(fullName));

  return `
    <div class="round-rail">
      <div class="rail-num">
        <span class="n">${position}</span>
        <span class="of">of ${ordered.length}</span>
      </div>
      <div class="rail-body">
        <div class="card-title">${escapeHtml(label)}</div>
        <p class="rail-title" title="${escapeHtml(fullName)}">${shownName}</p>
      </div>
    </div>
    <div class="seg-track">${segments}</div>`;
}

/** Position of a round within its plan, for the "Open Round N" button label. */
function roundPositionOf(rounds: RailRound[], activeId: number): number {
  const ordered = [...rounds].sort((a, b) => a.order - b.order);
  const zeroBased = ordered.findIndex((r) => r.id === activeId);
  return zeroBased >= 0 ? zeroBased + 1 : 1;
}

function renderRoundSelector(): void {
  const { eventContext } = state;
  if (!eventContext) return;

  const breakoutRounds = eventContext.rounds.filter(
    (r) => r.type === "one_on_one" || r.type === "huddle" || r.type === "static_huddle",
  );

  // Same event-name treatment the Lite screens use — one context bar under the
  // wordmark, not a badge beside it, so both modes read as the same product.
  const eventName = `<div class="context-bar"><span class="event-name">${escapeHtml(eventContext.event_name)}</span></div>`;

  if (breakoutRounds.length === 0) {
    mount(`
      <div class="header"><h1>Hoogah</h1></div>
      ${eventName}
      <div class="alert alert-info">No breakout rounds configured for this event.</div>`);
    return;
  }

  const activeRound = breakoutRounds.find((r) => r.id === eventContext.active_round_id);

  if (!activeRound) {
    // active_round_id is null only once every scheduled round is complete — the
    // event is over, so both modes land on the same wrap screen. A non-null
    // active round that isn't a breakout (a talk, a break) still shows the
    // waiting state below.
    if (eventContext.active_round_id === null) {
      renderWrapScreen(eventName, breakoutRounds.length, eventContext.participant_count);
      return;
    }
    mount(`
      <div class="header"><h1>Hoogah</h1></div>
      ${eventName}
      <div class="alert alert-info">No active round right now. Start a round from the Hoogah dashboard.</div>
      <div class="round-actions">
        <button class="btn btn-primary" id="refresh-round-btn">Refresh</button>
      </div>`);
    document.getElementById("refresh-round-btn")?.addEventListener("click", () => {
      void refreshEventContextAndRender();
    });
    return;
  }

  mount(`
    <div class="header"><h1>Hoogah</h1></div>
    ${eventName}
    ${roundRailHtml(breakoutRounds, activeRound.id, "Active round")}
    <div class="round-actions">
      <button class="btn btn-primary" id="load-round-btn">Open Round ${roundPositionOf(breakoutRounds, activeRound.id)}</button>
    </div>`);

  document.getElementById("load-round-btn")?.addEventListener("click", () => {
    void openActiveRound();
  });
}

async function openActiveRound(): Promise<void> {
  if (!state.auth || !state.eventId) return;
  renderLoading("Opening active round…");
  const meetingUuid = state.meetingUuid;
  try {
    state.eventContext = await fetchEventContext(
      requireAuth(),
      onTokenRefreshed,
      meetingUuid,
      DEV_MODE ? state.eventId : null,
    );
  } catch (err) {
    if (err instanceof AuthExpiredError) { renderError("Session expired. Please reopen the app."); return; }
    const msg = err instanceof ApiError ? `Failed to load event (${err.status})` : "Failed to load event";
    renderError(msg);
    return;
  }
  const ctx = state.eventContext;
  if (!ctx) {
    renderError("Failed to load event");
    return;
  }
  const activeRound = ctx.rounds.find(
    (r) =>
      r.id === ctx.active_round_id &&
      (r.type === "one_on_one" || r.type === "huddle" || r.type === "static_huddle"),
  );
  if (!activeRound) {
    renderRoundSelector();
    return;
  }
  state.selectedRoundId = activeRound.id;
  await loadAssignmentPlan();
}

async function loadAssignmentPlan(): Promise<void> {
  const { eventId, selectedRoundId } = state;
  if (!state.auth || !eventId || !selectedRoundId) return;

  renderLoading("Loading assignment plan…");
  // Lite's equivalent refresh (resyncLiteRound) re-reads the operator's role
  // before refetching, so a co-host promoted to host mid-round sees the
  // "Close breakout room" button unlock as soon as they hit Refresh. Full
  // events must do the same instead of trusting the role captured at open.
  await loadSelfRole(userContextGranted);
  try {
    state.plan = await fetchAssignmentPlan(requireAuth(), onTokenRefreshed, eventId, selectedRoundId);
    resetBreakoutState();
    await adoptSharedStage(state.plan);
    await loadMeetingIdentities();
    await refreshLiveParticipants(); // roster must be current before syncing it
    await syncEventParticipants(); // VE-026 AC-8 — re-classify registered vs walk-in
    renderDashboard();
  } catch (err) {
    if (err instanceof AuthExpiredError) { renderError("Session expired. Please reopen the app."); return; }
    const msg = err instanceof ApiError ? `API error ${err.status}` : "Failed to load assignment plan";
    renderError(msg);
  }
}

/* ════════════════════════════════════════════════════════════════════════
   FULL-EVENT vs LITE — sanctioned differences
   ════════════════════════════════════════════════════════════════════════
   Styling is deliberately identical across both modes: same tokens, same
   components, same DOM structure, one stylesheet (index.html). A visual
   difference between the two is a bug unless it appears in this list.

   As of VE-027, the next-round action and its host-only gate are also
   identical: both modes label it "Complete round & continue", both delete
   this round's rooms and record the run as finished before completing it
   server-side (handleFullRoundComplete() / handleLiteRoundAdvance()), and
   both gate the button on hostOnlyStepGuidance(state.selfRole). Refresh has
   been shared since VE-024 (both modes re-fetch from a server-published
   stage) and, since this fix, both re-read the operator's live Zoom role on
   refresh (loadSelfRole) before refetching — a co-host promoted to host
   mid-round must see the host-only gate unlock without reopening the app. No
   behavioural divergence remains to document here.

   Anything else — spacing, type, colour, control placement, the round
   selector, the context bar — must stay in lockstep. Prefer extracting a
   shared helper (see roundRailHtml) over copying markup between the two.
   ════════════════════════════════════════════════════════════════════════ */

function renderDashboard(): void {
  const { eventContext, plan } = state;
  if (!eventContext) return;

  const activeRound = eventContext.rounds.find((r) => r.id === state.selectedRoundId);
  const roundPill = activeRound
    ? `<span class="round-pill" title="${escapeHtml(activeRound.name)}">${escapeHtml(truncateName(activeRound.name))}</span>`
    : "";

  const assignmentSection = plan ? renderAssignmentPreview(plan, state, buildMatchingContext()) : "";
  // The error banner wins when it is already carrying the host-only message —
  // otherwise the same sentence appears twice on one screen.
  const hostNoteInErrorBanner = manualActionError === HOST_ONLY_STEP_MESSAGE;
  const actionSection = plan ? renderAssignmentActions(plan, state, hostNoteInErrorBanner) : "";
  const manualErrorBanner = manualActionError
    ? `<div class="alert alert-error">${escapeHtml(manualActionError)}</div>`
    : "";

  // Discard rooms is shown at "created" and "assigned" — in those stages we hide
  // Move to next round entirely (Discard is the only forward action). At "opened"
  // there's no Discard, but rooms must be closed first, so keep the disabled state.
  const hideNextRound =
    state.breakoutStage === "created" || state.breakoutStage === "assigned";
  const blockNextRound = state.breakoutStage === "opened";
  // Completing a round deletes its breakout rooms, which Zoom refuses for a
  // co-host (10001) — so block it once the role is actually known. An unknown
  // role must stay enabled: see hostOnlyStepGuidance().
  const blockNextRoundByRole = hostOnlyStepGuidance(state.selfRole).blocked;
  const nextRoundBtn = hideNextRound
    ? ""
    : blockNextRound
    ? `<button class="btn btn-primary" id="back-btn" disabled title="Close breakout rooms before moving to the next round">Complete round &amp; continue</button>`
    : blockNextRoundByRole
    ? `<button class="btn btn-primary" id="back-btn" disabled title="Only the meeting host can complete the round">Complete round &amp; continue</button>`
    : `<button class="btn btn-primary" id="back-btn">Complete round &amp; continue</button>`;

  mount(`
    <div class="header">
      <h1 id="home-link" class="home-link${blockNextRound ? " disabled" : ""}"${blockNextRound ? ' title="Close breakout rooms before leaving this round"' : ' title="Start over from round selection"'}>Hoogah</h1>
    </div>
    <div class="context-bar">
      <span class="event-name">${escapeHtml(eventContext.event_name)}</span>
      <div class="context-bar-row">
        ${roundPill}
        <button type="button" class="btn-refresh" id="full-refresh-btn" title="Sync with your team's current step">Refresh</button>
      </div>
    </div>
    ${manualErrorBanner}
    ${cleanupWarningHtml()}
    ${assignmentSection}
    <div class="action-bar">
      ${actionSection}
      ${nextRoundBtn}
    </div>`);

  if (plan) {
    bindBreakoutButtons({
      onCreate: () => runExclusiveStep(handleCreateRooms),
      onCreatePairs: () => runExclusiveStep(handleCreatePairs),
      onAssign: () => runExclusiveStep(handleAssignParticipants),
      onOpen: () => runExclusiveStep(handleOpenRooms),
      onClose: () => runExclusiveStep(handleCloseRooms),
      onDiscard: () => runExclusiveStep(handleDiscardRooms),
    });
    bindRoomAccordion();
    bindAbsentToggle();
    bindPlacementControls({
      onPlace: (attendeeKey, roomKey, liveUuid) => void handlePlaceParticipant(attendeeKey, roomKey, liveUuid),
      onReassign: (attendeeKey, fromRoomKey, toRoomKey, liveUuid) =>
        void handleReassignParticipant(attendeeKey, fromRoomKey, toRoomKey, liveUuid),
    });
  }

  document.getElementById("home-link")?.addEventListener("click", handleGoHome);
  document.getElementById("full-refresh-btn")?.addEventListener("click", () => void loadAssignmentPlan());

  if (!hideNextRound && !blockNextRound && !blockNextRoundByRole) {
    document.getElementById("back-btn")?.addEventListener("click", () => void handleFullRoundComplete());
  }
}

function renderCurrentDashboard(): void {
  if (state.isLite) {
    renderLiteDashboard();
  } else {
    renderDashboard();
  }
}

/** Banner for rooms a cleanup could not remove, or "" when the meeting is clean. */
function cleanupWarningHtml(): string {
  return roundCleanupWarning
    ? `<div class="alert alert-error">${escapeHtml(roundCleanupWarning)}</div>`
    : "";
}

/**
 * Leaves the embedded panel for an external page. zoomSdk.openUrl is the
 * sanctioned way to do this — a plain <a target="_blank"> doesn't reliably
 * open a browser tab from inside Zoom's app webview. window.open is tried
 * next (same defensive pattern as getUserContext elsewhere in this file),
 * but Zoom's embedded webview often blocks real popups too, so a top-level
 * location change is the last resort — that navigation gets handed off to
 * the system browser rather than silently doing nothing.
 */
async function openExternalUrl(url: string): Promise<void> {
  try {
    await zoomSdk.openUrl({ url });
  } catch (err) {
    void debugLog("openExternalUrl.sdk.failed", { url, error: serializeError(err) });
    const opened = window.open(url, "_blank", "noopener");
    if (!opened) {
      window.location.href = url;
    }
  }
}

function openHoogahAdmin(): Promise<void> {
  // zoom.hoogah.co is still "Allowed" (pending review) in the Marketplace
  // Domain Allow List, so zoomSdk.openUrl silently fails against it. The
  // Azure Static Web App behind it — same deployment, same content — is
  // already "Approved" under its default hostname; use that until the
  // custom domain clears review.
  return openExternalUrl("https://lemon-flower-0a6bc9e10.7.azurestaticapps.net/welcome");
}

/**
 * End-of-event screen, shared by both modes (VE-029): every round has run, the
 * participants are back in the main room, and the only thing left to offer is
 * the next event. Identical markup in full and Lite by design — see the
 * sanctioned-differences note above.
 */
function renderWrapScreen(eventNameHtml: string, roundCount: number, participantCount: number): void {
  mount(`
    <div class="header"><h1>Hoogah</h1></div>
    ${eventNameHtml}
    ${cleanupWarningHtml()}
    <div class="done-hero">
      <div class="done-badge">&#10003;</div>
      <div class="done-title">That's a wrap!</div>
      <p class="done-sub">Every round in your plan ran start to finish. Your participants are back in the main room.</p>
    </div>
    <div class="stat-row">
      <div class="stat"><div class="stat-num">${roundCount}</div><div class="stat-lbl">Round${roundCount !== 1 ? "s" : ""} run</div></div>
      <div class="stat"><div class="stat-num">${participantCount}</div><div class="stat-lbl">Participants</div></div>
    </div>
    <div class="promo">
      <div class="promo-eyebrow">Run it again</div>
      <div class="promo-title">Host your next virtual event with Hoogah</div>
      <p class="promo-copy">Upload a CSV, and Hoogah handles the breakout rooms, the pairings and the timing — every round, automatically.</p>
      <button class="btn btn-primary" id="create-new-event-btn">Create a new event &rarr;</button>
      <p class="promo-alt">or visit <a href="#" id="visit-hoogah-link">hoogah.co</a></p>
    </div>`);

  document.getElementById("create-new-event-btn")?.addEventListener("click", () => {
    void openHoogahAdmin();
  });
  document.getElementById("visit-hoogah-link")?.addEventListener("click", (e) => {
    e.preventDefault();
    void openExternalUrl("https://hoogah.co/");
  });
}

function renderLiteRoundSelector(): void {
  const { liteContext } = state;
  if (!liteContext) return;

  if (liteContext.rounds.length === 0) {
    mount(`
      <div class="header"><h1>Hoogah</h1></div>
      <div class="alert alert-info">No rounds found in your uploaded plan.</div>`);
    return;
  }

  const activeRound = liteContext.rounds.find((r) => r.id === liteContext.active_round_id);
  const eventName = liteContext.plan_title
    ? `<div class="context-bar"><span class="event-name">${escapeHtml(liteContext.plan_title)}</span></div>`
    : "";

  if (!activeRound) {
    renderWrapScreen(eventName, liteContext.rounds.length, liteContext.participant_count);
    return;
  }

  mount(`
    <div class="header"><h1>Hoogah</h1></div>
    ${eventName}
    ${cleanupWarningHtml()}
    ${roundRailHtml(liteContext.rounds, activeRound.id, "Current round")}
    <div class="round-actions">
      <button class="btn btn-primary" id="load-lite-round-btn">Open Round ${roundPositionOf(liteContext.rounds, activeRound.id)}</button>
    </div>`);

  document.getElementById("load-lite-round-btn")?.addEventListener("click", () => {
    state.selectedRoundId = activeRound.id;
    void loadLiteAssignmentPlan();
  });
}

/**
 * Loads the selected Lite round and renders it. Returns whether it landed on
 * the round dashboard — false means the round selector is showing instead, and
 * the caller must not paint the dashboard back over it.
 *
 * `selectorWhenNotStarted` exists so every operator lands where the host lands
 * after completing a round. The host's path (refreshLiteContextAndRender)
 * renders the selector without ever reading Zoom; a co-host pressing Refresh
 * used to load the plan and let adoptSharedStage() decide, which put them on
 * Assign whenever a leftover room from the previous round happened to share a
 * planned name — an organizer's CSV may well label every round "Breakout Room
 * 1", and a room a co-host cannot delete (Zoom 10001) survives to collide.
 * Gating on the *server's* recorded stage reaches the host's verdict instead.
 * The meeting still wins inside a round (AC-4) — it just no longer decides
 * which screen you land on.
 */
async function loadLiteAssignmentPlan(
  opts: { selectorWhenNotStarted?: boolean } = {},
): Promise<boolean> {
  const { selectedRoundId } = state;
  if (!state.auth || !selectedRoundId) return false;

  renderLoading("Loading assignment plan…");
  try {
    state.plan = await fetchLiteAssignmentPlan(requireAuth(), onTokenRefreshed, selectedRoundId);
    resetBreakoutState();
    await loadMeetingIdentities();
    if (opts.selectorWhenNotStarted && (state.plan.stage ?? "idle") === "idle") {
      renderLiteRoundSelector();
      return false;
    }
    await adoptSharedStage(state.plan);
    renderLiteDashboard();
    return true;
  } catch (err) {
    if (err instanceof AuthExpiredError) { renderError("Session expired. Please reopen the app."); return false; }
    if (err instanceof ApiError && err.status === 400) {
      renderError("This round needs more rooms than the per-meeting limit allows. Contact support to reduce round size.");
      return false;
    }
    const msg = err instanceof ApiError ? `API error ${err.status}` : "Failed to load assignment plan";
    renderError(msg);
    return false;
  }
}

/**
 * Reconcile server-recorded progress against the live meeting before adopting
 * it — the meeting is authoritative (AC-4). Costs one Zoom SDK call.
 */
async function adoptSharedStage(plan: AssignmentPlan): Promise<void> {
  let live: LiveBreakoutRooms | null = null;
  try {
    live = (await zoomSdk.getBreakoutRoomList()) as LiveBreakoutRooms;
  } catch (err) {
    void debugLog("resume.roomList.failed", { error: serializeError(err) });
  }
  const resume = resolveResumePoint(plan, live);
  state.breakoutStage = resume.stage;
  breakoutRoomByName = resume.roomByName;
  // Only while the rooms are open. Before that Zoom lists people as "assigned"
  // rather than "joined", so an occupancy read at the Assign step would report
  // every room empty and wipe the placements the operator just made.
  liveRoomOccupants = live?.state === "open" ? occupantsByRoom(live) : new Map();
  syncPlanToLiveOccupancy(plan);
  if (resume.stage !== (plan.stage ?? "idle")) {
    void publishAdoptedStage(resume.stage);
  }
}

/**
 * Re-seat the plan from what Zoom reports, for every room this client can see
 * into.
 *
 * The stored placements are scoped to one assignment run, so a refresh the
 * server answers with a different run (the previous run went terminal on
 * round-advance, or a fresh idle one was minted) hands back a plan with every
 * zoom_participant_id blank — and the round then rendered "0 inside" with two
 * people visibly sitting in the room. Zoom's own list is the fix: it describes
 * the meeting as it is now, not as some earlier run recorded it.
 *
 * Rooms absent from liveRoomOccupants are left exactly as the server described
 * them — that is a co-host, whose client Zoom tells nothing about occupancy.
 */
function syncPlanToLiveOccupancy(plan: AssignmentPlan): void {
  for (const room of plan.rooms) {
    const inside = liveRoomOccupants.get(room.room_name);
    if (!inside) continue;

    const insideByUuid = new Map(inside.map((o) => [o.participantUUID, o]));
    const unclaimed = new Set(insideByUuid.keys());

    // Anyone the stored placements still name correctly keeps their seat.
    for (const p of room.participants) {
      const uuid = p.zoom_participant_id ?? "";
      if (uuid && unclaimed.has(uuid)) {
        unclaimed.delete(uuid);
        continue;
      }
      // Zoom does not have them in this room — whatever the note says, they are
      // not inside. Clearing is what stops a stale note showing a green dot for
      // someone who left.
      p.zoom_participant_id = "";
    }

    // Whoever is left is in the room but unnamed by the note — the usual case
    // after a run switch, where every uuid was lost with it. Match them back by
    // screen name so the roster reads correctly, not just the count.
    for (const uuid of unclaimed) {
      const occupant = insideByUuid.get(uuid);
      const seat = room.participants.find(
        (p) => !p.zoom_participant_id && namesReferToSamePerson(p.display_name, occupant?.displayName),
      );
      if (seat) seat.zoom_participant_id = uuid;
    }
  }
}

/** Screen names Zoom and the CSV can plausibly disagree on — "SahilR" vs "Sahil Renapurkar". */
function namesReferToSamePerson(planName: string | null | undefined, liveName: string | null | undefined): boolean {
  if (!normalizeName(planName) || !normalizeName(liveName)) return false;
  return (
    normalizeName(planName) === normalizeName(liveName) ||
    isNameSubset(planName, liveName) ||
    isNamePrefixMatch(planName, liveName)
  );
}

/**
 * Push a stage inferred from the meeting back to the shared run.
 *
 * resolveResumePoint() treats the meeting as authoritative (AC-4), but only
 * locally. Unpublished, that verdict desynced the two: opening a round whose
 * rooms already exist in the meeting left the operator on "created" while the
 * shared run still said "idle", and confirmStepStillMine() then refused the
 * next step as stale. Sends the stage on its own so progress counters written
 * by another operator are not overwritten with this session's empty ones.
 */
async function publishAdoptedStage(stage: BreakoutStage): Promise<void> {
  const { plan, eventId } = state;
  if (!plan || !state.auth || (!state.isLite && !eventId)) return;
  try {
    if (state.isLite) {
      await updateLiteAssignmentRun(requireAuth(), onTokenRefreshed, plan.assignment_run_id, {
        stage,
      });
    } else {
      await updateAssignmentRun(requireAuth(), onTokenRefreshed, eventId!, plan.assignment_run_id, {
        stage,
      });
    }
  } catch (err) {
    void debugLog("stage.adopt.failed", { stage, error: serializeError(err) });
  }
}

function renderLiteDashboard(): void {
  const { liteContext, plan } = state;
  if (!liteContext) return;

  const activeRound = liteContext.rounds.find((r) => r.id === state.selectedRoundId);
  const roundPill = activeRound
    ? `<span class="round-pill" title="${escapeHtml(activeRound.name)}">${escapeHtml(truncateName(activeRound.name))}</span>`
    : "";
  const eventName = liteContext.plan_title
    ? `<span class="event-name">${escapeHtml(liteContext.plan_title)}</span>`
    : "";

  const assignmentSection = plan ? renderAssignmentPreview(plan, state, buildMatchingContext()) : "";
  // The error banner wins when it is already carrying the host-only message —
  // otherwise the same sentence appears twice on one screen.
  const hostNoteInErrorBanner = manualActionError === HOST_ONLY_STEP_MESSAGE;
  const actionSection = plan ? renderAssignmentActions(plan, state, hostNoteInErrorBanner) : "";
  const manualErrorBanner = manualActionError
    ? `<div class="alert alert-error">${escapeHtml(manualActionError)}</div>`
    : "";

  const hideNextRound = state.breakoutStage === "created" || state.breakoutStage === "assigned";
  const blockNextRound = state.breakoutStage === "opened";
  // Completing a round deletes its breakout rooms, which Zoom refuses for a
  // co-host (10001) — so block it once the role is actually known. An unknown
  // role must stay enabled: see hostOnlyStepGuidance().
  const blockNextRoundByRole = hostOnlyStepGuidance(state.selfRole).blocked;
  const nextRoundBtn = hideNextRound
    ? ""
    : blockNextRound
    ? `<button class="btn btn-primary" id="lite-next-round-btn" disabled title="Close breakout rooms before moving to the next round">Complete round &amp; continue</button>`
    : blockNextRoundByRole
    ? `<button class="btn btn-primary" id="lite-next-round-btn" disabled title="Only the meeting host can complete the round">Complete round &amp; continue</button>`
    : `<button class="btn btn-primary" id="lite-next-round-btn">Complete round &amp; continue</button>`;

  mount(`
    <div class="header">
      <h1 id="home-link" class="home-link${blockNextRound ? " disabled" : ""}"${blockNextRound ? ' title="Close breakout rooms before leaving this round"' : ' title="Sync with your team\'s current step"'}>Hoogah</h1>
    </div>
    <div class="context-bar">
      ${eventName}
      <div class="context-bar-row">
        ${roundPill}
        <button type="button" class="btn-refresh" id="lite-refresh-btn" title="Sync with your team's current step">Refresh</button>
      </div>
    </div>
    ${manualErrorBanner}
    ${cleanupWarningHtml()}
    ${assignmentSection}
    <div class="action-bar">
      ${actionSection}
      ${nextRoundBtn}
    </div>`);

  if (plan) {
    bindBreakoutButtons({
      onCreate: () => runExclusiveStep(handleCreateRooms),
      onCreatePairs: () => runExclusiveStep(handleCreatePairs),
      onAssign: () => runExclusiveStep(handleAssignParticipants),
      onOpen: () => runExclusiveStep(handleOpenRooms),
      onClose: () => runExclusiveStep(handleCloseRooms),
      onDiscard: () => runExclusiveStep(handleDiscardRooms),
    });
    bindRoomAccordion();
    bindAbsentToggle();
    bindPlacementControls({
      onPlace: (attendeeKey, roomKey, liveUuid) => void handlePlaceParticipant(attendeeKey, roomKey, liveUuid),
      onReassign: (attendeeKey, fromRoomKey, toRoomKey, liveUuid) =>
        void handleReassignParticipant(attendeeKey, fromRoomKey, toRoomKey, liveUuid),
    });
  }

  document.getElementById("home-link")?.addEventListener("click", handleGoHome);
  document.getElementById("lite-refresh-btn")?.addEventListener("click", () => void resyncLiteRound());

  if (!hideNextRound && !blockNextRound && !blockNextRoundByRole) {
    document.getElementById("lite-next-round-btn")?.addEventListener("click", () => {
      void handleLiteRoundAdvance();
    });
  }
}

/**
 * Re-syncs a Lite operator to the team's current step. Lite is a shared,
 * multi-operator flow — everyone must be able to catch up to whatever
 * stage the team has actually reached, without losing their place to a
 * screen nobody else is on (see openActiveLiteRound()/adoptSharedStage()).
 */
/**
 * Re-syncs a Lite operator to the team's current step, and re-reads the Zoom
 * roster so anyone who joined since Assign shows up as needing a room
 * (VE-023 AC-7). The two reads are unrelated — Hoogah owns the shared stage,
 * Zoom owns who is in the call — so a roster failure must not block the team
 * sync this button existed for first.
 */
async function resyncLiteRound(): Promise<void> {
  if (!state.isLite || !state.auth) return;
  renderLoading("Syncing with your team…");
  await loadSelfRole(userContextGranted);
  try {
    state.liteContext = await fetchLiteContext(requireAuth(), onTokenRefreshed);
    // A manual Refresh is the operator's acknowledgement of the leftover-rooms
    // warning — it must not follow them around for the rest of the event with
    // no way to dismiss it. noteCleanupLeftovers() will raise it again on the
    // next round-advance/discard if rooms are still stuck.
    roundCleanupWarning = null;
  } catch (err) {
    if (err instanceof AuthExpiredError) { renderError("Session expired. Please reopen the app."); return; }
    const msg = err instanceof ApiError ? `Failed to sync (${err.status})` : "Failed to sync";
    renderError(msg);
    return;
  }
  // reloads the plan, including persisted placements
  const onDashboard = await openActiveLiteRound();
  // The round has not started, so the selector is showing — the roster read
  // below only feeds the dashboard, and rendering it would paint over it.
  if (!onDashboard) return;
  try {
    await refreshLiveParticipants(); // no longer clears claims — see refreshLiveParticipants()
    renderLiteDashboard();
  } catch (err) {
    void debugLog("refresh.roster.failed", { error: serializeError(err) });
  }
}

function handleGoHome(): void {
  if (state.isLite) {
    void resyncLiteRound();
    return;
  }

  if (state.breakoutStage === "opened") return;

  if (state.breakoutStage === "created" || state.breakoutStage === "assigned") {
    const confirmed = window.confirm("Discard current round setup and go home?");
    if (!confirmed) return;
  }

  state.plan = null;
  state.selectedRoundId = null;
  resetBreakoutState();

  void refreshEventContextAndRender();
}

async function handleLiteRoundAdvance(): Promise<void> {
  const { selectedRoundId } = state;
  if (!state.auth || !selectedRoundId) return;

  renderLoading("Completing round…");
  try {
    // Before the server moves us on: strip this round's rooms out of the
    // meeting so the next round starts from a clean slate rather than
    // inheriting rooms that merely share a name. Leftovers are reported, not
    // fatal — the round advances either way.
    noteCleanupLeftovers(await deleteRoundRooms("round-advance"));
    // Only now is the run finished, so this is the one place a terminal status
    // is safe — see publishRoundOutcome() for what recording it earlier did.
    await publishRunFinished();
    await completeLiteRound(
      requireAuth(),
      onTokenRefreshed,
      selectedRoundId,
      state.zoomMeetingId,
      state.meetingUuid,
    );
    state.plan = null;
    state.selectedRoundId = null;
    resetBreakoutState();
    await refreshLiteContextAndRender();
  } catch (err) {
    if (err instanceof AuthExpiredError) { renderError("Session expired. Please reopen the app."); return; }
    const msg = err instanceof ApiError ? `API error ${err.status}` : "Failed to complete round";
    renderError(msg);
  }
}

async function refreshLiteContextAndRender(): Promise<void> {
  if (!state.auth) {
    renderError("Not authenticated");
    return;
  }
  renderLoading("Loading next round…");
  try {
    state.liteContext = await fetchLiteContext(requireAuth(), onTokenRefreshed);
  } catch (err) {
    if (err instanceof AuthExpiredError) { renderError("Session expired. Please reopen the app."); return; }
    const msg = err instanceof ApiError ? `Failed to load plan (${err.status})` : "Failed to load plan";
    renderError(msg);
    return;
  }
  renderLiteRoundSelector();
}

async function continueLiteAfterAuth(): Promise<void> {
  renderLoading("Loading your plan…");
  try {
    state.liteContext = await fetchLiteContext(requireAuth(), onTokenRefreshed);
  } catch (err) {
    if (err instanceof AuthExpiredError) { renderError("Session expired. Please reopen the app."); return; }
    const msg = err instanceof ApiError ? `Failed to load plan (${err.status})` : "Failed to load plan";
    renderError(msg);
    return;
  }
  await openActiveLiteRound();
}

/** Returns whether the round dashboard is showing; false means the selector is. */
async function openActiveLiteRound(): Promise<boolean> {
  const activeRoundId = state.liteContext?.active_round_id;
  if (!activeRoundId) {
    renderLiteRoundSelector();
    return false;
  }
  state.selectedRoundId = activeRoundId;
  return loadLiteAssignmentPlan({ selectorWhenNotStarted: true });
}

/**
 * Full-event counterpart of handleLiteRoundAdvance() (VE-027) — same shape:
 * clear this round's rooms out of the meeting, record the run as finished,
 * then complete the round server-side so the dashboard and every other
 * operator's app agree the round is done (AC-1, AC-5, AC-6).
 */
async function handleFullRoundComplete(): Promise<void> {
  const { selectedRoundId, eventId } = state;
  if (!state.auth || !selectedRoundId || !eventId) return;

  renderLoading("Completing round…");
  try {
    noteCleanupLeftovers(await deleteRoundRooms("round-advance"));
    await publishRunFinished();
    const result = await completeAssignmentRound(requireAuth(), onTokenRefreshed, eventId, selectedRoundId);
    if (result.already_complete) {
      void debugLog("round.complete.already_complete", { roundId: selectedRoundId });
    }
    state.plan = null;
    state.selectedRoundId = null;
    resetBreakoutState();
    await refreshEventContextAndRender();
  } catch (err) {
    if (err instanceof AuthExpiredError) { renderError("Session expired. Please reopen the app."); return; }
    const msg = err instanceof ApiError ? `Failed to complete round (${err.status})` : "Failed to complete round";
    renderError(msg);
  }
}

async function refreshEventContextAndRender(): Promise<void> {
  if (!state.auth || !state.eventId) {
    renderRoundSelector();
    return;
  }
  renderLoading("Loading next round…");
  await loadSelfRole(userContextGranted);
  const meetingUuid = state.meetingUuid;
  try {
    state.eventContext = await fetchEventContext(
      requireAuth(),
      onTokenRefreshed,
      meetingUuid,
      DEV_MODE ? state.eventId : null,
    );
  } catch (err) {
    if (err instanceof AuthExpiredError) { renderError("Session expired. Please reopen the app."); return; }
    const msg = err instanceof ApiError ? `Failed to load event (${err.status})` : "Failed to load event";
    renderError(msg);
    return;
  }
  renderRoundSelector();
}

async function reportStepFailure(step: string, err: unknown): Promise<void> {
  const { plan, eventId } = state;
  const msg = err instanceof Error ? err.message : String(err);
  void debugLog("breakout.failed", { step, operators: operatorNames(), error: serializeError(err) });
  state.breakoutStage = "failed";
  const failurePayload = {
    status: "failed" as const,
    error_summary: `[${step}] ${msg}`.slice(0, 1000),
    room_assignments: breakoutRoomAssignments,
    assigned_participant_count: breakoutAssignedCount,
    unassigned_participant_count: breakoutUnassignedCount,
  };
  if (plan && state.auth && state.isLite) {
    try {
      await updateLiteAssignmentRun(requireAuth(), onTokenRefreshed, plan.assignment_run_id, failurePayload);
    } catch {
      // ignore — primary error already surfaced
    }
  } else if (plan && state.auth && eventId) {
    try {
      await updateAssignmentRun(requireAuth(), onTokenRefreshed, eventId, plan.assignment_run_id, failurePayload);
    } catch {
      // ignore — primary error already surfaced
    }
  }
  renderError(`Step "${step}" failed — ${msg}`);
}

/**
 * Publish the current step so other hosts/co-hosts resume here.
 * Deliberately swallows failures: the Zoom action has already happened and
 * must not be rolled back or re-surfaced as an error mid-meeting (AC-N4).
 */
async function publishStage(stage: BreakoutStage): Promise<void> {
  const { plan, eventId } = state;
  if (!plan || !state.auth || (!state.isLite && !eventId)) return;
  const payload = {
    stage,
    assigned_participant_count: breakoutAssignedCount,
    unassigned_participant_count: breakoutUnassignedCount,
    room_assignments: breakoutRoomAssignments,
  };
  try {
    if (state.isLite) {
      await updateLiteAssignmentRun(requireAuth(), onTokenRefreshed, plan.assignment_run_id, payload);
    } else {
      await updateAssignmentRun(requireAuth(), onTokenRefreshed, eventId!, plan.assignment_run_id, payload);
    }
  } catch (err) {
    void debugLog("stage.publish.failed", { stage, error: serializeError(err) });
  }
}

/**
 * Publish the closing step and everything the round produced — but no status.
 *
 * The server treats a terminal status as "run finished" and stops resuming the
 * run, so whoever reads the plan next gets a brand-new run at stage "idle".
 * With the round's rooms still sitting in the meeting, resolveResumePoint()
 * then infers "created" and adoptSharedStage() publishes that, rewriting a
 * finished round backwards to its second step. Recording the outcome at Open
 * broke Close the same way; the run is only finished when the round is
 * completed, so roundOutcomeStatus() is sent from handleLiteRoundAdvance().
 */
/**
 * Mark the run finished as the round is completed. Best-effort: the round must
 * advance even if this write fails, or an operator is stuck on a done round.
 */
async function publishRunFinished(): Promise<void> {
  const { plan, eventId } = state;
  if (!plan || !state.auth || (!state.isLite && !eventId)) return;
  const payload: AssignmentRunUpdate = { status: roundOutcomeStatus() };
  try {
    if (state.isLite) {
      await updateLiteAssignmentRun(requireAuth(), onTokenRefreshed, plan.assignment_run_id, payload);
    } else {
      await updateAssignmentRun(requireAuth(), onTokenRefreshed, eventId!, plan.assignment_run_id, payload);
    }
  } catch (err) {
    void debugLog("stage.finish.failed", { error: serializeError(err) });
  }
}

/** applied/partial/failed for the round just finished, from what actually placed. */
function roundOutcomeStatus(): AssignmentRunUpdate["status"] {
  if (breakoutUnassignedCount === 0 && breakoutErrors.length === 0) return "applied";
  return breakoutAssignedCount > 0 ? "partial" : "failed";
}

async function publishRoundOutcome(stage: BreakoutStage): Promise<void> {
  const { plan, eventId } = state;
  if (!plan || !state.auth || (!state.isLite && !eventId)) return;
  const payload: AssignmentRunUpdate = {
    stage,
    assigned_participant_count: breakoutAssignedCount,
    unassigned_participant_count: breakoutUnassignedCount,
    error_summary: breakoutErrors.join("; ").slice(0, 1000),
    room_assignments: breakoutRoomAssignments,
  };
  try {
    if (state.isLite) {
      await updateLiteAssignmentRun(requireAuth(), onTokenRefreshed, plan.assignment_run_id, payload);
    } else {
      await updateAssignmentRun(requireAuth(), onTokenRefreshed, eventId!, plan.assignment_run_id, payload);
    }
  } catch (err) {
    void debugLog("stage.outcome.failed", { stage, error: serializeError(err) });
  }
}

/**
 * Returns true if the caller should proceed. Re-reads the shared plan first so
 * two operators on the same round cannot both run the same step unknowingly
 * (AC-5). Fails open on a read error — a live meeting must never be blocked
 * by a failed staleness check.
 */
async function confirmStepStillMine(action: string): Promise<boolean> {
  const { selectedRoundId, eventId } = state;
  if (!state.auth || !selectedRoundId || (!state.isLite && !eventId)) return true;

  let fresh: AssignmentPlan;
  try {
    fresh = state.isLite
      ? await fetchLiteAssignmentPlan(requireAuth(), onTokenRefreshed, selectedRoundId)
      : await fetchAssignmentPlan(requireAuth(), onTokenRefreshed, eventId!, selectedRoundId);
  } catch (err) {
    void debugLog("stage.recheck.failed", { error: serializeError(err) });
    return true;
  }

  const theirStage = fresh.stage ?? "idle";
  if (theirStage === state.breakoutStage) return true;

  // Zoom's app webview blocks modal dialogs, so window.confirm() used to return
  // false here without ever showing anything — the click died silently, with no
  // SDK call and no log line, and the dashboard repainted unchanged. Re-sync to
  // the shared stage and say so in the banner instead: the operator can then
  // press the step the round is actually on.
  void debugLog("stage.mismatch", { theirStage, ourStage: state.breakoutStage, action });
  state.plan = fresh;
  await adoptSharedStage(fresh);
  // adoptSharedStage lands on what the meeting shows, which is not always the
  // stage the team recorded — this client may not see their rooms at all. Only
  // point at the step above when we actually moved to it.
  manualActionError =
    state.breakoutStage === theirStage
      ? stageMovedMessage(theirStage, action)
      : stageOutOfViewMessage(theirStage, action);
  renderLiteDashboard();
  return false;
}

/**
 * Reads the live meeting roster and rebuilds every lookup tier used by
 * resolveLiveEntry(). Called at the start of both Create and Assign — an
 * operator who lands on Assign without having run Create in this page
 * session (a co-host, or a host who reopened the app) must not see empty
 * maps, and the roster can change between Create and Assign anyway.
 */
async function refreshLiveParticipants(): Promise<void> {
  const live = await zoomSdk.getMeetingParticipants();
  const index = buildLiveIndex(live.participants ?? [], knownEmailsByUuid);
  breakoutLiveById = index.byId;
  breakoutLiveByName = index.byName;
  breakoutLiveByEmail = index.byEmail;
  breakoutLiveByNormalizedName = index.byNormalizedName;
  breakoutLiveEntriesByUuid = index.byUuid;
  liveParticipantsRaw = index.raw;
  operatorLiveUuids = index.operatorUuids;
  // claimedLiveUuids is NOT cleared here: this function also runs from a
  // mid-round Refresh (VE-023 AC-7), where forgetting who is already seated
  // would surface the whole meeting as needing placement. Callers that start
  // a fresh assignment run clear it themselves before calling.

  void debugLog("breakout.live", {
    count: index.raw.length,
    names: Array.from(index.byName.keys()),
    emailsIndexed: index.byEmail.size,
    // Confirms the roster actually carries role — the only operator signal
    // left now that getUserContext is refused (80004/app_not_support).
    roles: (live.participants ?? []).map((p) => `${p.screenName}:${p.role ?? "?"}`),
  });

}

/**
 * Syncs the live Zoom roster against Hoogah registrations for this event
 * (VE-026 AC-1/AC-3). No-op for Lite. Runs once at init and again on every
 * Refresh (loadAssignmentPlan) so late joiners get correctly classified.
 * A sync failure is swallowed — degrades to "treat everyone as unregistered
 * until the next successful sync" rather than blocking the caller.
 */
async function syncEventParticipants(): Promise<void> {
  if (state.isLite || !state.eventId) return;
  try {
    const participantsResult = await zoomSdk.getMeetingParticipants();
    const zoomParticipants = (participantsResult.participants ?? []).map((p) => ({
      participant_id: p.participantId,
      participant_uuid: p.participantUUID,
      display_name: p.screenName,
      email: (p as unknown as { email?: string }).email ?? "",
    }));
    state.syncResult = await syncParticipants(requireAuth(), onTokenRefreshed, state.eventId, zoomParticipants);
  } catch (err) {
    console.warn("Participant sync failed:", err);
  }
}

/**
 * Asks Zoom to collect participant emails — which prompts *every* person in the
 * meeting, host and co-host included.
 *
 * Called only when name matching has actually failed somebody. Zoom does not
 * remember a prior grant: every call re-prompts the whole meeting, and each
 * operator running the app is a separate client that would prompt again. Firing
 * it on every Create, as this used to, meant a meeting where names matched
 * cleanly still interrupted everyone for data it never needed.
 *
 * The emails do not arrive in time to help the run that triggered the request —
 * the ack only confirms the prompt went out, and addresses land later, one per
 * consent, on onParticipantEmail. They reach the *next* Assign or Refresh
 * through knownEmailsByUuid, which is why this stays worth asking at all.
 */
async function requestParticipantEmails(reason: string): Promise<void> {
  if (!emailCapabilityGranted || emailConsentRequested) return;
  emailConsentRequested = true;
  try {
    const ack = await zoomSdk.getMeetingParticipantsEmail({
      reasonForAsking: "To match you to your assigned breakout room automatically.",
    });
    // Logged so a run with zero emails can be told apart from a run where the
    // request itself never happened.
    void debugLog("email.consent.requested", { reason, ack: (ack as { message?: string })?.message ?? "" });
  } catch (err) {
    // Non-fatal — matching falls through to the name tiers.
    void debugLog("email.consent.failed", {
      reason,
      code: zoomErrorCode(err),
      error: serializeError(err),
    });
  }
}

/**
 * True when an email prompt could plausibly place someone the name tiers just
 * missed. Nobody is interrupted for a plan that carries no emails to match on.
 */
function emailsCouldHelpUnmatched(): boolean {
  return state.unmatchedAssignments.some((row) => Boolean(row.email));
}

/**
 * Guards the breakout step handlers against re-entry. Zoom's panel delivers a
 * double click (or a click plus a re-render's click) often enough that Open
 * Rooms ran twice in the same second, the second call landing on already-open
 * rooms and reporting 10121 as a failure.
 */
let stepInFlight = false;

async function runExclusiveStep(fn: () => Promise<void>): Promise<void> {
  if (stepInFlight) return;
  stepInFlight = true;
  try {
    await fn();
  } finally {
    stepInFlight = false;
  }
}

/**
 * Generates this round's pairs from the sheet — same PairingService call
 * admin_app's "Create Pairs" button makes — then reloads the plan so the
 * newly created rooms replace the empty state and "Create Breakout Rooms"
 * appears in its place.
 */
async function handleCreatePairs(): Promise<void> {
  const { eventId, selectedRoundId } = state;
  if (!state.auth || !eventId || !selectedRoundId) return;

  renderLoading("Creating pairs…");
  try {
    await createRoundPairs(requireAuth(), onTokenRefreshed, eventId, selectedRoundId);
  } catch (err) {
    if (err instanceof AuthExpiredError) { renderError("Session expired. Please reopen the app."); return; }
    const msg = err instanceof ApiError ? `Failed to create pairs (${err.status})` : "Failed to create pairs";
    renderError(msg);
    return;
  }
  await loadAssignmentPlan();
}

async function handleCreateRooms(): Promise<void> {
  const { plan, eventId } = state;
  if (!plan || !state.auth || (!state.isLite && !eventId)) return;
  if (!(await confirmStepStillMine("create the rooms again"))) return;

  renderLoading("Creating breakout rooms…");

  let step = "init";
  try {
    step = "getMeetingParticipants";
    claimedLiveUuids = new Set(); // fresh run: nobody is placed yet
    await refreshLiveParticipants();

    const names = plan.rooms.map((r) => r.room_name);
    if (names.length === 0) {
      renderError("Plan has no rooms.");
      return;
    }

    step = "createBreakoutRooms";
    const created = await zoomSdk.createBreakoutRooms({
      assign: "manually",
      names,
      numberOfRooms: names.length,
    });

    breakoutRoomByName = new Map();
    for (const r of created.rooms ?? []) {
      breakoutRoomByName.set(r.name, r.breakoutRoomId);
    }
    void debugLog("breakout.created", {
      requested: names,
      returned: (created.rooms ?? []).map((r) => r.name),
    });

    step = "getBreakoutRoomList";
    const startMs = Date.now();
    let confirmed = false;
    let lastListError = "";
    while (Date.now() - startMs < 3000) {
      try {
        const list = await zoomSdk.getBreakoutRoomList();
        const listed = list.rooms ?? [];
        if (listed.length >= names.length) {
          breakoutRoomByName = new Map();
          for (const r of listed) breakoutRoomByName.set(r.name, r.breakoutRoomId);
          confirmed = true;
          break;
        }
      } catch (listErr) {
        // Zoom throws "No Breakout Room exist." until the rooms settle, so a
        // failure here is only meaningful once the whole window has elapsed.
        lastListError = listErr instanceof Error ? listErr.message : String(listErr);
      }
      await new Promise((r) => setTimeout(r, 200));
    }

    // Previously this fell through to stage "created" even when the rooms never
    // appeared, so a silent no-op create surfaced much later as "No Breakout
    // Room exist." from Open Rooms, pointing at the wrong step.
    if (!confirmed) {
      void debugLog("breakout.created.unconfirmed", { expected: names.length, lastListError });
      throw new Error(
        `Zoom did not report the ${names.length} breakout room(s) after creating them` +
          (lastListError ? ` — ${lastListError}` : ""),
      );
    }

    state.breakoutStage = "created";
    void publishStage("created");
    renderCurrentDashboard();
  } catch (err) {
    await reportStepFailure(step, err);
  }
}

async function handleAssignParticipants(): Promise<void> {
  const { plan, eventId } = state;
  if (!plan || !state.auth || (!state.isLite && !eventId)) return;
  if (!(await confirmStepStillMine("assign participants again"))) return;

  renderLoading("Assigning participants…");

  breakoutRoomAssignments = [];
  breakoutAssignedCount = 0;
  breakoutUnassignedCount = 0;
  breakoutErrors = [];
  claimedLiveUuids = new Set();
  manualActionError = null;
  state.unmatchedAssignments = [];

  let step = "getMeetingParticipants";
  try {
    // Always re-read the roster: this operator may never have run Create in
    // this page session (co-host, or host after reopening the app), and
    // people join between Create and Assign.
    await refreshLiveParticipants();

    step = "assignParticipantToBreakoutRoom";
    for (const room of plan.rooms) {
      const roomUuid = breakoutRoomByName.get(room.room_name);
      if (!roomUuid) {
        breakoutErrors.push(`Room not created: ${room.room_name}`);
        for (const p of room.participants) {
          breakoutUnassignedCount += 1;
          breakoutRoomAssignments.push({
            user_id: p.user_id,
            pairing_id: p.pairing_id,
            email: p.email,
            zoom_room_name: room.room_name,
            assignment_status: "failed",
            error_message: "room_not_created",
          });
          state.unmatchedAssignments.push({
            display_name: p.display_name,
            email: p.email,
            room_name: room.room_name,
            reason: "Room wasn't created",
            key: attendeeKeyOf(p),
            room_key: room.room_key,
          });
        }
        continue;
      }

      for (const participant of room.participants) {
        const key = attendeeKeyOf(participant);
        const liveEntry = resolveLiveEntry(participant);

        if (!liveEntry) {
          breakoutUnassignedCount += 1;
          const hadCandidate = findRawLiveCandidate(participant) !== null;
          upsertRoomAssignment(participant, room.room_name, participant.zoom_participant_id ?? "", "skipped");
          state.unmatchedAssignments.push({
            display_name: participant.display_name,
            email: participant.email,
            room_name: room.room_name,
            reason: hadCandidate ? "Already matched to someone else — needs manual pick" : "Not found in meeting",
            key,
            room_key: room.room_key,
          });
          continue;
        }

        try {
          await zoomSdk.assignParticipantToBreakoutRoom({
            uuid: roomUuid,
            participantUUID: liveEntry.entry.uuid,
          });
          claimedLiveUuids.add(liveEntry.entry.uuid);
          breakoutAssignedCount += 1;
          participant.zoom_participant_id = liveEntry.entry.uuid;
          upsertRoomAssignment(participant, room.room_name, liveEntry.entry.uuid, "assigned", liveEntry.matchedBy);
        } catch (err) {
          breakoutUnassignedCount += 1;
          const msg = err instanceof Error ? err.message : String(err);
          breakoutErrors.push(`${participant.display_name}: ${msg}`);
          upsertRoomAssignment(participant, room.room_name, liveEntry.entry.uuid, "failed", liveEntry.matchedBy);
          state.unmatchedAssignments.push({
            display_name: participant.display_name,
            email: participant.email,
            room_name: room.room_name,
            reason: "Assign failed",
            key,
            room_key: room.room_key,
          });
        }
      }
    }

    state.breakoutStage = "assigned";
    void publishStage("assigned");
    renderCurrentDashboard();

    // Only now is it known whether names were enough. Anyone still unmatched is
    // a reason to ask; a clean run is not. Deliberately after the render so the
    // prompt does not sit on top of a loading screen, and not awaited — the
    // addresses are for the next run either way.
    if (emailsCouldHelpUnmatched()) {
      void requestParticipantEmails("unmatched-after-assign");
    }
  } catch (err) {
    await reportStepFailure(step, err);
  }
}

/** VE-017 AC-1: remember an organiser-confirmed Zoom name against the attendee's record so
 *  it's recognised automatically at future events. Never called for an automatic match. */
async function maybeSaveNameAlias(participant: RoomParticipant, displayName: string): Promise<void> {
  if (state.isLite || !state.eventId || !participant.user_id || !state.auth) return;
  try {
    await saveZoomNameAlias(requireAuth(), onTokenRefreshed, state.eventId, participant.user_id, displayName);
  } catch (err) {
    void debugLog("alias.save.failed", { error: serializeError(err) });
  }
}

/**
 * Pull in every confirmation made in this meeting, whoever made it.
 *
 * A host and a co-host taking turns each held their own copy of
 * manualPlacements, so one operator's decision was invisible to the other and
 * the same attendee kept reappearing in the Place list round after round. Best
 * effort: a failed read costs the shared decisions, not the round.
 */
async function loadMeetingIdentities(): Promise<void> {
  const { eventId } = state;
  if (!state.auth || !state.meetingUuid || (!state.isLite && !eventId)) return;
  try {
    const identities = state.isLite
      ? await fetchLiteMeetingIdentities(requireAuth(), onTokenRefreshed, state.meetingUuid)
      : await fetchMeetingIdentities(requireAuth(), onTokenRefreshed, eventId!, state.meetingUuid);
    for (const identity of identities) {
      // A decision made in this tab is the fresher one — it has not been sent
      // yet when this runs on the round that made it.
      if (manualPlacements.has(identity.attendee_key)) continue;
      manualPlacements.set(identity.attendee_key, {
        uuid: identity.zoom_participant_id,
        screenName: identity.screen_name,
      });
    }
  } catch (err) {
    void debugLog("identities.load.failed", { error: serializeError(err) });
  }
}

/**
 * Record an organiser's placement decision for every operator, not just this
 * tab. Best effort for the same reason publishStage() is: the participant has
 * already been moved in Zoom, and that must not be undone because a write
 * failed.
 */
async function rememberPlacement(attendeeKey: string, liveOption: LiveParticipantOption): Promise<void> {
  manualPlacements.set(attendeeKey, liveOption);
  const { eventId } = state;
  if (!state.auth || !state.meetingUuid || (!state.isLite && !eventId)) return;
  try {
    const identity = {
      attendee_key: attendeeKey,
      zoom_participant_id: liveOption.uuid,
      screen_name: liveOption.screenName,
    };
    if (state.isLite) {
      await saveLiteMeetingIdentity(requireAuth(), onTokenRefreshed, state.meetingUuid, identity);
    } else {
      await saveMeetingIdentity(requireAuth(), onTokenRefreshed, eventId!, state.meetingUuid, identity);
    }
  } catch (err) {
    void debugLog("identities.save.failed", { error: serializeError(err) });
  }
}

/** VE-015 AC-7: organiser directly places an unmatched participant. */
async function handlePlaceParticipant(attendeeKey: string, roomKey: string, liveUuid: string): Promise<void> {
  const { plan } = state;
  if (!plan) return;

  const room = plan.rooms.find((r) => r.room_key === roomKey);
  const participant = room?.participants.find((p) => attendeeKeyOf(p) === attendeeKey);
  const liveOption = liveParticipantsRaw.find((l) => l.uuid === liveUuid);
  const roomUuid = room && breakoutRoomByName.get(room.room_name);
  if (!room || !participant || !liveOption || !roomUuid) return;

  try {
    await zoomSdk.assignParticipantToBreakoutRoom({ uuid: roomUuid, participantUUID: liveUuid });
    claimedLiveUuids.add(liveUuid);
    participant.zoom_participant_id = liveUuid;
    void rememberPlacement(attendeeKeyOf(participant), { uuid: liveUuid, screenName: liveOption.screenName });
    upsertRoomAssignment(participant, room.room_name, liveUuid, "assigned", "manual");
    breakoutAssignedCount += 1;
    breakoutUnassignedCount = Math.max(0, breakoutUnassignedCount - 1);
    state.unmatchedAssignments = state.unmatchedAssignments.filter(
      (r) => !(r.key === attendeeKeyOf(participant) && r.room_key === roomKey),
    );
    manualActionError = null;
    renderCurrentDashboard();
    await maybeSaveNameAlias(participant, liveOption.screenName);
  } catch (err) {
    manualActionError = err instanceof Error ? err.message : "Couldn't place that participant";
    renderCurrentDashboard();
  }
}

/** VE-015 AC-9/AC-10: organiser corrects a placement already made — a different room, a
 *  different identity, or both. The correction is remembered for the rest of the meeting. */
async function handleReassignParticipant(
  attendeeKey: string,
  fromRoomKey: string,
  toRoomKey: string,
  liveUuid: string,
): Promise<void> {
  const { plan } = state;
  if (!plan) return;

  const fromRoom = plan.rooms.find((r) => r.room_key === fromRoomKey);
  const toRoom = plan.rooms.find((r) => r.room_key === toRoomKey);
  const participant = fromRoom?.participants.find((p) => attendeeKeyOf(p) === attendeeKey);
  const liveOption = liveParticipantsRaw.find((l) => l.uuid === liveUuid);
  const toRoomUuid = toRoom && breakoutRoomByName.get(toRoom.room_name);
  if (!fromRoom || !toRoom || !participant || !liveOption || !toRoomUuid) return;

  try {
    await zoomSdk.assignParticipantToBreakoutRoom({ uuid: toRoomUuid, participantUUID: liveUuid });
    const previousUuid = participant.zoom_participant_id;
    if (previousUuid && previousUuid !== liveUuid) claimedLiveUuids.delete(previousUuid);
    claimedLiveUuids.add(liveUuid);
    participant.zoom_participant_id = liveUuid;
    void rememberPlacement(attendeeKey, { uuid: liveUuid, screenName: liveOption.screenName });

    if (fromRoomKey !== toRoomKey) {
      fromRoom.participants = fromRoom.participants.filter((p) => p !== participant);
      toRoom.participants.push(participant);
    }
    upsertRoomAssignment(participant, toRoom.room_name, liveUuid, "assigned", "manual");
    manualActionError = null;
    renderCurrentDashboard();
    await maybeSaveNameAlias(participant, liveOption.screenName);
  } catch (err) {
    manualActionError = err instanceof Error ? err.message : "Couldn't reassign that participant";
    renderCurrentDashboard();
  }
}

interface CleanupResult {
  /** Rooms with a planned name still in the meeting after the deletes ran. */
  remaining: number;
  /** True when the round had rooms but this client could see none of them to delete. */
  blind: boolean;
}

/** The meeting's room list, or null when this client cannot read it. */
async function safeRoomList(reason: string): Promise<LiveBreakoutRooms | null> {
  try {
    return (await zoomSdk.getBreakoutRoomList()) as LiveBreakoutRooms;
  } catch (err) {
    void debugLog("breakout.cleanup.list.failed", { reason, error: serializeError(err) });
    return null;
  }
}

function plannedRoomNames(): string[] {
  return (state.plan?.rooms ?? []).map((room) => room.room_name);
}

/**
 * Remove this round's breakout rooms from the meeting; returns how many are
 * still there afterwards.
 *
 * Closing rooms only returns people to the main session — Zoom keeps the rooms
 * themselves. Left behind, they collide with the next round by name (an
 * organizer's CSV may well label every round "Breakout Room 1"), and
 * resolveResumePoint() then matches the stale room and reports the next round
 * as already created.
 *
 * The meeting's own list is read every time, not only as a fallback for an
 * empty cache. breakoutRoomByName is keyed by *name*, so it holds one UUID per
 * name — when two operators each ran Create, Zoom holds two rooms per name and
 * the cache can only ever name one of them. Deleting from the cache alone left
 * the other operator's duplicates in the meeting: the host's client showed a
 * clean slate while every co-host still saw the previous round's rooms. Both
 * sources are unioned and deduped by UUID so one client's blind spot cannot
 * silently strand a room.
 *
 * Deleting per room stays best-effort — a room that refuses to go (Zoom returns
 * 10001 to a co-host on deleteBreakoutRoom) must never block the round from
 * advancing — but the caller is told what survived rather than assuming none did.
 */
async function deleteRoundRooms(reason: string): Promise<CleanupResult> {
  const planned = plannedRoomNames();
  const uuids = cleanupTargetUuids(
    breakoutRoomByName.values(),
    await safeRoomList(reason),
    planned,
  );

  if (uuids.length === 0) {
    // Nothing to delete is only good news if this round never had rooms. Past
    // "idle" it did, so seeing none means this client has lost sight of them —
    // the same blind spot stageOutOfViewMessage() covers — and the rooms are
    // most likely still in the meeting for everybody else.
    const blind = state.breakoutStage !== "idle";
    void debugLog("breakout.cleanup.nothing_visible", { reason, stage: state.breakoutStage, blind });
    return { remaining: 0, blind };
  }

  let failed = 0;
  for (const uuid of uuids) {
    try {
      await zoomSdk.deleteBreakoutRoom({ uuid });
    } catch (err) {
      failed += 1;
      void debugLog("breakout.cleanup.delete.failed", {
        reason,
        uuid,
        code: zoomErrorCode(err),
        error: serializeError(err),
      });
    }
  }

  // Verify against the meeting rather than trusting the delete calls: a room
  // this client cannot see was never in `uuids` to begin with, so a clean run
  // of deletes is not evidence the meeting is clear.
  const remaining = cleanupTargetUuids([], await safeRoomList(`${reason}.verify`), planned).length;
  void debugLog("breakout.cleanup.done", { reason, attempted: uuids.length, failed, remaining });
  return { remaining, blind: false };
}

/**
 * Warn when cleanup left rooms behind. Surfaced instead of failing: the round
 * must still advance, but the operator needs to know the meeting is not clean —
 * otherwise the next round inherits rooms that merely share a name, and every
 * other operator keeps seeing the round that just ended.
 */
function noteCleanupLeftovers(result: CleanupResult): void {
  if (result.remaining > 0) {
    roundCleanupWarning =
      `${result.remaining} breakout room${result.remaining === 1 ? "" : "s"} from this round could not be removed. ` +
      `Ask the meeting host to delete them in Zoom's Breakout Rooms panel before the next round starts.`;
    return;
  }
  roundCleanupWarning = result.blind
    ? "This Zoom client cannot see this round's breakout rooms, so they may still be in the meeting for " +
      "everyone else. Check Zoom's Breakout Rooms panel and remove any left over before the next round starts."
    : null;
}

async function handleDiscardRooms(): Promise<void> {
  if (!state.plan) return;
  if (!(await confirmStepStillMine("discard the rooms"))) return;

  renderLoading("Discarding breakout rooms…");

  let step = "deleteBreakoutRoom";
  try {
    noteCleanupLeftovers(await deleteRoundRooms("discard"));
    resetBreakoutState();
    void publishStage("idle");
    renderCurrentDashboard();
  } catch (err) {
    await reportStepFailure(step, err);
  }
}

/**
 * Handles a failed Open/Close Breakout Rooms call. With the co-host gate
 * removed, two operators can press the same button at once — the loser gets
 * Zoom 10121/10092 ("already open"/"already closed") and should re-sync
 * instead of seeing a failure. 10100/10101 mean the Zoom account setting
 * "Allow co-hosts to manage breakout rooms" is off — the only signal for
 * that, since the SDK does not expose the setting itself.
 */
async function handleBreakoutSdkError(step: string, err: unknown): Promise<void> {
  const code = zoomErrorCode(err);
  // Logged before any branching so an unmapped code is still captured with the
  // role that hit it — serializeError() drops .code off Error instances.
  void debugLog("breakout.sdk.error", { step, code, operators: operatorNames(), error: serializeError(err) });

  if (state.isLite && isAlreadyDoneCode(code)) {
    void debugLog("breakout.race", { step, operators: operatorNames(), error: serializeError(err) });
    await resyncLiteRound();
    return;
  }
  const friendly = breakoutErrorMessage(err);
  if (friendly) {
    manualActionError = friendly;
    void debugLog("breakout.blocked", { step, operators: operatorNames(), error: serializeError(err) });
    renderCurrentDashboard();
    return;
  }
  await reportStepFailure(step, err);
}

async function handleCloseRooms(): Promise<void> {
  const { plan, eventId } = state;
  if (!plan || !state.auth || (!state.isLite && !eventId)) return;
  if (!(await confirmStepStillMine("close the rooms"))) return;

  renderLoading("Closing breakout rooms…");

  let step = "closeBreakoutRooms";
  try {
    await zoomSdk.closeBreakoutRooms();
    state.breakoutStage = "closed";
    void publishRoundOutcome("closed");
    renderCurrentDashboard();
  } catch (err) {
    await handleBreakoutSdkError(step, err);
  }
}

async function handleOpenRooms(): Promise<void> {
  const { plan, eventId } = state;
  if (!plan || !state.auth || (!state.isLite && !eventId)) return;
  if (!(await confirmStepStillMine("open the rooms"))) return;

  renderLoading("Opening rooms…");

  let step = "init";
  try {
    // Read the list first: "No Breakout Room exist." from configureBreakoutRooms
    // is ambiguous between the rooms genuinely being gone and this operator
    // being refused, and only the list separates the two.
    try {
      const list = await zoomSdk.getBreakoutRoomList();
      void debugLog("breakout.open.roomList", {
        rooms: (list.rooms ?? []).map((r) => r.name),
        state: (list as { state?: string }).state ?? "",
      });
    } catch (listErr) {
      void debugLog("breakout.open.roomList.failed", { error: serializeError(listErr) });
    }

    // Best-effort, never fatal. This call only sets convenience flags, but Zoom
    // rejects it with 10065 "No Breakout Room exist." in the very same tick that
    // getBreakoutRoomList returns the rooms — an undocumented code for this API
    // (the docs list only 10099). Blocking Open on it stranded the whole round;
    // without it participants get Zoom's Join prompt instead of being moved.
    try {
      await zoomSdk.configureBreakoutRooms(
        buildBreakoutConfig(state.isLite ? state.liteContext : state.eventContext),
      );
    } catch (configErr) {
      void debugLog("breakout.configure.skipped", {
        code: zoomErrorCode(configErr),
        error: serializeError(configErr),
      });
    }

    step = "openBreakoutRooms";
    await zoomSdk.openBreakoutRooms();

    state.breakoutStage = "opened";

    // Deliberately no `status` here. A terminal status (applied/partial/failed)
    // marks the run finished, and _find_resumable_run() on the server excludes
    // finished runs — so the next assignment-plan read minted a *fresh* run at
    // stage "idle". confirmStepStillMine() then compared local "opened" against
    // that "idle" and refused every later step, making Close and Discard
    // permanently unreachable once the rooms were open. Open is mid-round; the
    // outcome is recorded at Close instead.
    const finalPayload: AssignmentRunUpdate = {
      stage: "opened",
      assigned_participant_count: breakoutAssignedCount,
      unassigned_participant_count: breakoutUnassignedCount,
      error_summary: breakoutErrors.join("; ").slice(0, 1000),
      room_assignments: breakoutRoomAssignments,
    };

    try {
      if (state.isLite) {
        await updateLiteAssignmentRun(requireAuth(), onTokenRefreshed, plan.assignment_run_id, finalPayload);
      } else {
        await updateAssignmentRun(requireAuth(), onTokenRefreshed, eventId!, plan.assignment_run_id, finalPayload);
      }
    } catch (err) {
      void debugLog("assignment-run.update.failed", { error: serializeError(err) });
    }

    renderCurrentDashboard();
  } catch (err) {
    await handleBreakoutSdkError(step, err);
  }
}

/**
 * Generate a Zoom Apps PKCE pair.
 *
 * Zoom Apps SDK only supports the `plain` PKCE method
 * (https://developers.zoom.us/docs/zoom-apps/authentication/), which means:
 *   code_challenge === code_verifier  (no hashing)
 *
 * The verifier MUST be a URL-safe random string of 43–128 characters
 * (RFC 7636 unreserved characters). We use base64url of 32 random bytes → 43 chars.
 */
function generatePkcePair(): { codeVerifier: string; codeChallenge: string } {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let str = "";
  for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
  const codeVerifier = btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  return { codeVerifier, codeChallenge: codeVerifier };
}

const DEV_MODE = import.meta.env.VITE_DEV_MODE === "true";

function renderDevLogin(): void {
  mount(`
    <div class="header">
      <h1>Hoogah <span style="font-size:12px;color:#f59e0b;font-weight:normal">[DEV MODE]</span></h1>
    </div>
    <div class="card">
      <div class="card-title">Dev Login</div>
      <input id="dev-email" type="email" placeholder="your@email.com" style="width:100%;padding:8px;border:1px solid #e2e8f0;border-radius:6px;font-size:14px;box-sizing:border-box" />
      <input id="dev-event-id" type="number" placeholder="Event ID (optional)" style="width:100%;padding:8px;border:1px solid #e2e8f0;border-radius:6px;font-size:14px;margin-top:8px;box-sizing:border-box" />
      <button class="btn btn-primary" id="dev-login-btn" style="margin-top:12px">Login</button>
      <div id="dev-error" style="color:#dc2626;font-size:13px;margin-top:8px"></div>
    </div>`);

  document.getElementById("dev-login-btn")?.addEventListener("click", async () => {
    const email = (document.getElementById("dev-email") as HTMLInputElement).value.trim();
    const eventIdInput = (document.getElementById("dev-event-id") as HTMLInputElement).value.trim();
    const errorEl = document.getElementById("dev-error")!;
    if (!email) { errorEl.textContent = "Email required"; return; }
    errorEl.textContent = "";

    try {
      // DevAuthView returns the legacy single-token response.
      const devResp = await devAuth(email, eventIdInput ? parseInt(eventIdInput, 10) : undefined);
      // Wrap in HoogahAuth — dev tokens have no refresh token.
      state.auth = { access: devResp.token, refresh: "", accessExpiresAt: devResp.expires_at, refreshExpiresAt: "" };
      state.eventId = devResp.event_id;
      state.isLite = devResp.mode === "lite";
    } catch (err) {
      errorEl.textContent = err instanceof ApiError ? `Login failed (${err.status})` : "Login failed";
      return;
    }

    if (state.isLite) {
      await continueLiteAfterAuth();
      return;
    }

    await continueAfterAuth("");
  });
}

async function continueAfterAuth(meetingUuid: string): Promise<void> {
  if (!state.eventId) {
    renderError("No event linked. Enter an Event ID.");
    return;
  }

  renderLoading("Loading event…");

  try {
    state.eventContext = await fetchEventContext(
      requireAuth(),
      onTokenRefreshed,
      meetingUuid,
      DEV_MODE ? state.eventId : null,
    );
  } catch (err) {
    if (err instanceof AuthExpiredError) { renderError("Session expired. Please reopen the app."); return; }
    const msg = err instanceof ApiError ? `Failed to load event (${err.status})` : "Failed to load event";
    renderError(msg);
    return;
  }

  renderRoundSelector();
}

function subscribeBreakoutCloseEvents(): void {
  const sdk = zoomSdk as unknown as {
    onMeetingConfigChanged?: (cb: (e: { change?: string }) => void) => void;
  };
  if (typeof sdk.onMeetingConfigChanged !== "function") return;
  sdk.onMeetingConfigChanged((event) => {
    const change = event?.change ?? "";
    if (!change.includes("breakoutRoomClosed")) return;
    if (state.breakoutStage !== "opened") return;
    state.breakoutStage = "closed";
    // The native panel is the documented fallback when Zoom refuses the app's
    // own close (co-hosts always hit that). Clear the failure banner: leaving it
    // up next to a completed Close step reads as a contradiction.
    manualActionError = null;
    // Closing from Zoom's own panel is a supported route, so record the outcome
    // exactly as the in-app Close does — otherwise the shared stage stays
    // "opened" and the next step is refused as stale for every operator.
    void publishRoundOutcome("closed");
    renderCurrentDashboard();
  });
}

interface SdkConfig {
  configured: boolean;
  emailCapabilityGranted: boolean;
  userContextGranted: boolean;
}

/**
 * Read the operator's own meeting role, if Zoom will tell us.
 *
 * Only the host may close breakout rooms or complete a round — Zoom returns
 * 10001 to a co-host on both, despite documenting Co-Host as supported. Knowing
 * the role lets the app disable those steps for a co-host instead of letting
 * them fail. Any failure here leaves selfRole null, which the views treat as
 * "unknown" and fall back to advisory copy with the buttons live: a wrong guess
 * would lock the host out of their own round.
 */
async function loadSelfRole(granted: boolean): Promise<void> {
  if (!granted) {
    void debugLog("selfRole.unavailable", { reason: "getUserContext not declared on the listing" });
    return;
  }
  try {
    const context = await zoomSdk.getUserContext();
    state.selfRole = (context.role as OperatorRole) ?? null;
    void debugLog("selfRole.resolved", { role: state.selfRole, status: context.status });
  } catch (err) {
    void debugLog("selfRole.failed", { code: zoomErrorCode(err), error: serializeError(err) });
  }
}

async function configureZoomSdk(): Promise<SdkConfig> {
  try {
    const configResponse = await zoomSdk.config({
      capabilities: [
        "getAppContext",
        "getMeetingContext",
        // Requested even while it may be undeclared on the Marketplace listing:
        // an undeclared capability comes back in configResponse.unsupportedApis
        // rather than failing config(), so asking costs nothing and the app
        // starts gating Close/Complete-round by role the moment Zoom approves
        // it — no redeploy. Until then selfRole stays null and every operator
        // keeps live buttons plus an advisory note.
        "getUserContext",
        "getMeetingParticipants",
        "getMeetingParticipantsEmail",
        "getRunningContext",
        "authorize",
        "onAuthorized",
        "createBreakoutRooms",
        "configureBreakoutRooms",
        "openBreakoutRooms",
        "closeBreakoutRooms",
        "deleteBreakoutRoom",
        "getBreakoutRoomList",
        "assignParticipantToBreakoutRoom",
        "onBreakoutRoomChange",
        "onMeetingConfigChanged",
        "openUrl",
      ],
      popoutSize: { width: 480, height: 720 },
      version: "0.16",
    });
    if (configResponse) subscribeBreakoutCloseEvents();
    // ConfigResponse has no "capabilities" field — a capability is granted unless
    // Zoom lists it in unsupportedApis (sdk.d.ts's actual response shape).
    const unsupported: string[] = configResponse?.unsupportedApis ?? [];
    const emailCapabilityGranted = !unsupported.includes("getMeetingParticipantsEmail");
    const userContextGranted = !unsupported.includes("getUserContext");
    // Log the names, not just a count — a refused capability here surfaces later
    // as an opaque 80004/app_not_support at the call site, and the name is the
    // only thing that points at the Marketplace listing as the fix.
    void debugLog("zoomSdk.config", { emailCapabilityGranted, userContextGranted, unsupported });
    return { configured: Boolean(configResponse), emailCapabilityGranted, userContextGranted };
  } catch (err) {
    void debugLog("zoomSdk.config.failed", { error: serializeError(err) });
    return { configured: false, emailCapabilityGranted: false, userContextGranted: false };
  }
}

async function init(): Promise<void> {
  if (DEV_MODE) {
    await configureZoomSdk();
    renderDevLogin();
    return;
  }

  renderLoading("Initializing Hoogah…");

  const sdkConfig = await configureZoomSdk();
  if (!sdkConfig.configured) {
    // Not inside a Zoom client — send a plain-browser visitor to the marketing
    // page instead of a dead-end error. Runs before any auth code below.
    window.location.replace("/welcome");
    return;
  }
  emailCapabilityGranted = sdkConfig.emailCapabilityGranted;
  userContextGranted = sdkConfig.userContextGranted;
  if (emailCapabilityGranted) {
    zoomSdk.onParticipantEmail((event) => handleParticipantEmailEvent(event));
  }
  await loadSelfRole(userContextGranted);

  renderLoading("Authenticating…");

  let contextToken: string;
  try {
    const appContext = await zoomSdk.getAppContext();
    contextToken = appContext.context;
  } catch (err) {
    renderError("Could not retrieve Zoom app context. Ensure the app is launched from within a meeting.");
    return;
  }

  const { codeVerifier, codeChallenge } = generatePkcePair();

  let oauthCode: string;
  try {
    oauthCode = await runAuthorizeFlow(codeChallenge);
    void debugLog("authorize.code", { oauthCodePrefix: oauthCode.slice(0, 12) });
  } catch (err) {
    console.error("authorize flow failed:", err);
    void debugLog("authorize.failed", { error: serializeError(err) });
    const detail = err instanceof Error ? `${err.name}: ${err.message}` : JSON.stringify(err);
    renderError(`OAuth failed — ${detail}`);
    return;
  }

  // Fetch numeric meeting ID via the SDK; the backend uses this to link the
  // Zoom meeting to a Hoogah event when the saved config has no UUID yet.
  let zoomMeetingId = "";
  try {
    const meetingCtx = await zoomSdk.getMeetingContext();
    zoomMeetingId = meetingCtx.meetingID || "";
    void debugLog("getMeetingContext", {
      meetingID: zoomMeetingId,
      meetingTopic: meetingCtx.meetingTopic,
    });
  } catch (err) {
    void debugLog("getMeetingContext.failed", { error: serializeError(err) });
  }
  state.zoomMeetingId = zoomMeetingId;


  try {
    const authResp = await exchangeContextToken(
      contextToken,
      oauthCode,
      codeVerifier,
      zoomMeetingId,
    );
    state.auth = {
      access: authResp.access_token,
      refresh: authResp.refresh_token,
      accessExpiresAt: authResp.access_expires_at,
      refreshExpiresAt: authResp.refresh_expires_at,
    };
    state.eventId = authResp.event_id;
    // Straight from the "mid" claim of the context token the server just
    // verified. Read here rather than from the Zoom SDK: getMeetingUUID() needs
    // a Marketplace capability we do not hold, and answers with the breakout
    // room's UUID when called from inside one — which would split the
    // meeting-wide placement decisions in half without ever erroring.
    state.meetingUuid = authResp.zoom_meeting_uuid ?? "";
    state.isLite = authResp.mode === "lite";
  } catch (err) {
    void debugLog("exchange.failed", {
      error: serializeError(err),
      baseUrl: BASE_URL,
      contextTokenPrefix: contextToken.slice(0, 24),
      oauthCodePrefix: oauthCode.slice(0, 12),
      codeVerifierPrefix: codeVerifier.slice(0, 12),
      zoomMeetingId,
    });
    renderError(authErrorMessage(err));
    return;
  }

  if (state.isLite) {
    await continueLiteAfterAuth();
    return;
  }

  if (!state.eventId) {
    renderError("This Zoom meeting is not linked to a Hoogah event.");
    return;
  }

  const meetingUuid = state.meetingUuid;

  renderLoading("Syncing participants…");

  await syncEventParticipants();

  await continueAfterAuth(meetingUuid);
}

init().catch((err) => {
  console.error("Unhandled init error:", err);
  renderError("Unexpected error. Please close and reopen the app.");
});
